import {
  IWorkOrderService,
  SearchByNameResponseType,
  WorkOrderSummary
} from './contracts/workOrders.service';

import { GetFullProductionWorkOrdersParams } from './contracts/production.service';
import apiClient from '../../api/index';
import { createUrl } from '_clean-code/utils/urlHelper';

export enum WorkOrderStatus2 {
  Backlog = 'Backlog',
  Scheduled = 'Scheduled',
  InProgress = 'InProgress',
  Programmed = 'Programmed',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Reviewed = 'Reviewed'
}

export const WorkOrderStatuses2 = [
  WorkOrderStatus2.Backlog,
  WorkOrderStatus2.Scheduled,
  WorkOrderStatus2.InProgress,
  WorkOrderStatus2.Completed,
  WorkOrderStatus2.Cancelled,
  WorkOrderStatus2.Reviewed
];

export const WorkOrderService: IWorkOrderService = {
  getWorkOrderSummaries: async (
    name?: string,
    code?: string,
    status?: string[],
    id?: string[],
    machineIds?: number[],
    includeNoMachine?: boolean
  ): Promise<WorkOrderSummary[]> => {
    const url = createUrl('/work-orders')
      .addQueryParam('view', 'summary')
      .addQueryParam('pageSize', 20);

    if (includeNoMachine) {
      url.addQueryParam('includeNoMachine', true);
    }

    if (machineIds && machineIds.length > 0) {
      url.addQueryParamMany('machines', machineIds);
    }
    if (code) {
      url.addQueryParam('code', code);
    }
    if (name) {
      url.addQueryParam('name', name);
    }
    if (status && status.length > 0) {
      url.addQueryParamMany('status', status);
    }
    if (id && id.length > 0) {
      url.addQueryParamMany('id', id);
    }
    const response = await apiClient.request('GET', url.toStringUrl());

    return response as WorkOrderSummary[];
  },
  search: async (_params: GetFullProductionWorkOrdersParams): Promise<any> => {
    const params: any = {};
    if (_params.workOrder) {
      params.workOrder = { search: _params.workOrder };
    }
    if (_params.code.length > 0) {
      params.workOrder = { include: _params.code.map(({ value }) => value) };
    }
    if (_params.description) {
      params.description = { search: _params.description };
    }
    if (_params.machineId.length > 0) {
      params.machine = { include: _params.machineId };
    }
    if (_params.skuId.length > 0) {
      params.sku = { include: _params.skuId };
    }
    if (_params.skuTarget) {
      params.skuTarget = {
        [_params.skuTarget.type === 'equals'
          ? 'equal'
          : _params.skuTarget.type]: _params.skuTarget.filter
      };
    }
    if (_params.priority) {
      params.priority = {
        [_params.priority.type === 'equals' ? 'equal' : _params.priority.type]:
          _params.priority.filter
      };
    }
    if (_params.progress) {
      params.progress = {
        [_params.progress.type === 'equals' ? 'equal' : _params.progress.type]:
          _params.progress.filter
      };
    }
    if (_params.workOrderStatus.length > 0) {
      params.workOrderStatus = { include: _params.workOrderStatus };
    }
    if (_params.start) {
      params.deliveryDate = {
        ...(params.deliveryDate ?? {}),
        after: _params.start
      };
    }
    if (_params.end) {
      params.deliveryDate = {
        ...(params.deliveryDate ?? {}),
        before: _params.end
      };
    }
    if (_params.start && _params.end) {
      params.deliveryDate = {
        between: { start: _params.start, end: _params.end }
      };
    }
    const response = await apiClient.request(
      'POST',
      createUrl('/work-orders/filters').toStringUrl(),
      params
    );
    return response.data as any[];
  },
  updatePriority: async function (id: string, priority: number) {
    return apiClient.request(
      'PATCH',
      createUrl(`/work-orders/${id}`).toStringUrl(),
      { priority }
    );
  },
  updateStatus: async function (id: string, status) {
    return apiClient.request(
      'PATCH',
      createUrl(`/work-orders/${id}`).toStringUrl(),
      { status }
    );
  },
  bulkDelete: async function (ids: string[]) {
    return apiClient.request(
      'DELETE',
      createUrl(`/work-orders`).toStringUrl(),
      { ids }
    );
  },
  delete: async function (id: string) {
    return apiClient.request(
      'DELETE',
      createUrl(`/work-orders/${id}`).toStringUrl()
    );
  },
  searchByName: async function (code: string, pageSize: number) {
    const response = await apiClient.request(
      'GET',
      `/work-orders/search?code=${code}&pageSize=${pageSize}`
    );
    return response as SearchByNameResponseType;
  },
  getRawMaterials: async function (woId: string) {
    const response = await apiClient.request(
      'GET',
      createUrl(`/work-orders/${woId}/raw-materials`).toStringUrl()
    );
    return response as any[];
  }
};
